<template>
  <b-modal v-model="modal" title="Importar Convidados" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true" hide-footer>
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>

    <!-- Step 1: Instructions -->
    <div v-if="step === 1">
      <div class="text-center mb-4">
        <div class="import-icon-wrapper mb-3">
          <i class="bx bx-spreadsheet"></i>
        </div>
        <h5 class="mb-1">Importação de convidados</h5>
        <p class="text-muted mb-0">Cadastre vários convidados de uma vez usando uma planilha Excel ou CSV. Os dados preenchidos já voltam preenchidos no formulário.</p>
      </div>

      <!-- Seletor de formulário (quando há mais de um) -->
      <div v-if="forms.length > 1" class="card border shadow-none mb-3">
        <div class="card-body p-3">
          <label for="form" class="font-size-13 mb-1 d-block">
            <i class="bx bx-list-ul text-primary mr-1"></i> Formulário
          </label>
          <select class="custom-select" id="form" v-model="formId" @change="onFormChange">
            <option v-for="f in forms" :key="f.id" :value="f.id">{{ f.name }}{{ f.group_name ? ' — ' + f.group_name : '' }}</option>
          </select>
          <small class="text-muted">As colunas do modelo seguem o formulário escolhido.</small>
        </div>
      </div>

      <!-- How it works -->
      <div class="card border shadow-none mb-3">
        <div class="card-body p-3">
          <h6 class="mb-3"><i class="bx bx-info-circle text-primary mr-1"></i> Como funciona</h6>
          <div class="steps-list">
            <div class="step-item">
              <span class="step-number">1</span>
              <div>
                <strong>Baixe o modelo</strong>
                <p class="text-muted mb-0 font-size-12">O modelo já vem com as colunas do seu formulário (inclusive os campos do Form Builder).</p>
              </div>
            </div>
            <div class="step-item">
              <span class="step-number">2</span>
              <div>
                <strong>Preencha os dados</strong>
                <p class="text-muted mb-0 font-size-12">Uma linha por convidado. O <strong>E-mail</strong> é obrigatório.</p>
              </div>
            </div>
            <div class="step-item">
              <span class="step-number">3</span>
              <div>
                <strong>Faça o upload</strong>
                <p class="text-muted mb-0 font-size-12">Convidados com e-mail já existente serão <strong>atualizados</strong>; novos serão criados.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Columns legend -->
      <div class="card border shadow-none mb-3">
        <div class="card-body p-3">
          <h6 class="mb-3"><i class="bx bx-columns text-primary mr-1"></i> Colunas da planilha</h6>
          <div class="table-responsive" style="max-height: 280px; overflow-y: auto;">
            <table class="table table-sm table-bordered mb-0 font-size-13">
              <thead class="thead-light">
                <tr>
                  <th style="width: 38%">Coluna</th>
                  <th style="width: 14%">Tipo</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(col, i) in columns" :key="i">
                  <td>
                    <code>{{ col.coluna }}</code>
                    <span v-if="col.obrigatorio" class="badge badge-soft-danger font-size-10 ml-1">obrigatório</span>
                    <span v-if="col.origem === 'custom'" class="badge badge-soft-info font-size-10 ml-1">personalizado</span>
                    <span v-if="col.origem === 'acompanhante'" class="badge badge-soft-warning font-size-10 ml-1">acompanhante</span>
                  </td>
                  <td><span class="text-muted">{{ col.tipo }}</span></td>
                  <td>{{ col.descricao }}</td>
                </tr>
                <tr v-if="!columns.length">
                  <td colspan="3" class="text-muted text-center">Nenhum formulário ativo encontrado para gerar o modelo.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Format selector (só com acompanhante) + download -->
      <!-- Formato da planilha (só quando o evento é "Com acompanhante") -->
      <div v-if="withCompanion" class="form-group mb-4">
        <label for="layout" class="font-size-13 mb-1 d-block">Formato da planilha</label>
        <select class="custom-select w-100" id="layout" v-model="layout">
          <option value="inline">Lado a lado (principal + acompanhantes na mesma linha)</option>
          <option value="stacked">Empilhado (1 linha por pessoa + vínculo)</option>
        </select>
      </div>

      <!-- Download template button -->
      <div class="text-center mb-3">
        <button type="button" class="btn btn-outline-success" :disabled="downloading" @click="baixarModelo()">
          <i class="bx bx-download mr-1"></i> {{ downloading ? 'Gerando...' : 'Baixar Template (.xlsx)' }}
        </button>
      </div>

      <hr class="mb-3 mt-1" />

      <div class="d-flex justify-content-between">
        <b-button variant="light" @click="close()">Cancelar</b-button>
        <b-button variant="primary" @click="step = 2">
          Já tenho a planilha pronta <i class="bx bx-right-arrow-alt ml-1"></i>
        </b-button>
      </div>
    </div>

    <!-- Step 2: Upload -->
    <div v-if="step === 2">
      <div class="text-center mb-4">
        <div class="import-icon-wrapper mb-3">
          <i class="bx bx-cloud-upload"></i>
        </div>
        <h5 class="mb-1">Enviar planilha</h5>
        <p class="text-muted mb-0">Selecione o arquivo preenchido para iniciar a importação.</p>
      </div>

      <div
        class="drop-zone mb-3"
        :class="{ 'drop-zone-active': isDragging, 'drop-zone-has-file': file }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <input ref="fileInput" type="file" accept=".xlsx,.xls,.csv" class="d-none" @change="handleFileSelect" />
        <div v-if="!file" class="text-center">
          <i class="bx bx-cloud-upload drop-zone-icon text-muted"></i>
          <p class="mb-1">Arraste o arquivo aqui ou <strong class="text-primary">clique para selecionar</strong></p>
          <small class="text-muted">Formatos aceitos: .xlsx, .xls, .csv</small>
        </div>
        <div v-else class="text-center">
          <i class="bx bx-file drop-zone-icon text-success"></i>
          <p class="mb-1 font-weight-medium">{{ file.name }}</p>
          <small class="text-muted">{{ formatFileSize(file.size) }}</small>
          <br />
          <a href="#" @click.stop.prevent="file = null" class="text-danger font-size-12 mt-1 d-inline-block">
            <i class="bx bx-x"></i> Remover arquivo
          </a>
        </div>
      </div>

      <div class="alert alert-info font-size-13 mb-3 d-flex align-items-start" role="alert">
        <i class="bx bx-bulb mr-2 font-size-16 mt-0"></i>
        <div>
          <strong>Dica:</strong> convidados com o mesmo <strong>e-mail</strong> são atualizados; novos e-mails criam novos convidados.
        </div>
      </div>

      <hr class="mb-3 mt-1" />

      <div class="d-flex justify-content-between">
        <b-button variant="light" @click="step = 1">
          <i class="bx bx-left-arrow-alt mr-1"></i> Voltar
        </b-button>
        <b-button variant="primary" :disabled="!file || loading" @click="applyImport()">
          <i class="bx bx-upload mr-1"></i> Importar Convidados
        </b-button>
      </div>
    </div>

    <!-- Step 3: Processing + Result -->
    <div v-if="step === 3">
      <div v-if="processing" class="text-center py-4">
        <div class="import-icon-wrapper mb-3">
          <i class="bx bx-loader-alt bx-spin"></i>
        </div>
        <h5 class="mb-2">Processando planilha...</h5>
        <p class="text-muted mb-3">Aguarde enquanto os convidados são importados.</p>
        <div class="progress mx-auto" style="max-width: 400px; height: 8px;">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" role="progressbar" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <p class="text-muted font-size-13 mt-2 mb-0">{{ progressPercent }}%</p>
      </div>

      <div v-else>
        <div class="text-center">
          <div class="import-icon-wrapper mb-3" :class="{ 'import-icon-success': !hasErrors, 'import-icon-warning': hasErrors }">
            <i :class="hasErrors ? 'bx bx-error' : 'bx bx-check-circle'"></i>
          </div>
          <h5 class="mb-2">Importação concluída!</h5>
        </div>

        <div class="row justify-content-center mt-3 mb-3">
          <div class="col-auto">
            <div class="result-card result-card-success">
              <span class="result-number">{{ importResult.imported }}</span>
              <span class="result-label">importados</span>
            </div>
          </div>
          <div class="col-auto" v-if="importResult.errors > 0">
            <div class="result-card result-card-danger">
              <span class="result-number">{{ importResult.errors }}</span>
              <span class="result-label">erros</span>
            </div>
          </div>
        </div>

        <p class="text-muted text-center" v-if="!hasErrors">Todos os convidados foram processados com sucesso.</p>

        <div v-if="errorLogEntries.length > 0" class="mt-3">
          <h6 class="mb-2"><i class="bx bx-x-circle text-danger mr-1"></i> Linhas com erro</h6>
          <div class="border rounded" style="max-height: 250px; overflow-y: auto; background: #f8f9fa;">
            <div
              v-for="(entry, i) in errorLogEntries"
              :key="i"
              class="d-flex align-items-start py-2 px-3"
              :class="{ 'border-bottom': i < errorLogEntries.length - 1 }"
            >
              <i class="bx bx-x-circle text-danger mr-2 mt-1 font-size-14"></i>
              <div class="font-size-13">
                <strong v-if="entry.identifier">{{ entry.identifier }}</strong>
                <span v-if="entry.identifier && entry.name"> — </span>
                <span v-if="entry.name">{{ entry.name }}</span>
                <span v-if="entry.message" class="d-block text-danger font-size-12 mt-1">{{ entry.message }}</span>
              </div>
            </div>
          </div>
        </div>

        <hr class="mb-3 mt-3" />

        <div class="text-center">
          <b-button variant="primary" @click="finish()">Fechar</b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { errorMessage } from '@/components/alertsMessages'
import { importUsers, downloadTemplate, importColumns, importForms } from '@/pages/guests/services'

export default {
  name: 'participant-import',
  components: {
    Loading
  },
  props: {},
  data () {
    return {
      modal: false,
      loading: false,
      step: 1,
      file: null,
      isDragging: false,
      layout: 'inline',
      downloading: false,
      withCompanion: false,
      forms: [],
      formId: null,
      columns: [],
      processing: false,
      progressPercent: 0,
      progressTimer: null,
      importResult: {
        imported: 0,
        errors: 0,
        log: []
      }
    }
  },
  computed: {
    hasErrors () {
      return this.importResult.errors > 0
    },
    errorLogEntries () {
      return (this.importResult.log || []).filter(e => e.status === 'error')
    }
  },
  methods: {
    open () {
      this.step = 1
      this.file = null
      this.isDragging = false
      this.layout = 'inline'
      this.forms = []
      this.formId = null
      this.processing = false
      this.progressPercent = 0
      this.importResult = { imported: 0, errors: 0, log: [] }
      this.modal = true
      this.loadForms()
    },
    async loadForms () {
      try {
        const res = await importForms()
        this.forms = res.data.forms || []
        this.formId = this.forms.length ? this.forms[0].id : null
      } catch (err) {
        this.forms = []
        this.formId = null
      }
      await this.loadColumns()
    },
    onFormChange () {
      this.loadColumns()
    },
    async loadColumns () {
      try {
        const res = await importColumns({ formId: this.formId })
        this.columns = res.data.columns || []
        this.withCompanion = !!res.data.with_companion
        if (!this.withCompanion) this.layout = 'inline'
      } catch (err) {
        this.columns = []
      }
    },
    async baixarModelo () {
      this.downloading = true
      try {
        const res = await downloadTemplate({ formId: this.formId, layout: this.layout })
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = this.layout === 'stacked'
          ? 'modelo_importacao_convidados_empilhado.xlsx'
          : 'modelo_importacao_convidados_lado_a_lado.xlsx'
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        errorMessage('Alerta: não foi possível gerar o modelo de importação.')
      } finally {
        this.downloading = false
      }
    },
    handleFileSelect (e) {
      const files = e.target.files
      if (files && files.length > 0) {
        this.file = files[0]
      }
    },
    handleDrop (e) {
      this.isDragging = false
      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        const file = files[0]
        const ext = file.name.split('.').pop().toLowerCase()
        if (['xlsx', 'xls', 'csv'].includes(ext)) {
          this.file = file
        } else {
          errorMessage('Alerta: Formato não suportado. Use .xlsx, .xls ou .csv')
        }
      }
    },
    formatFileSize (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },
    async applyImport () {
      if (!this.file) return
      this.processing = true
      this.progressPercent = 0
      this.step = 3
      this.startProgress()
      try {
        const formData = new FormData()
        formData.append('file', this.file, this.file.name)
        if (this.formId) formData.append('form_id', this.formId)
        const res = await importUsers(formData)
        this.importResult = {
          imported: res.data.imported || 0,
          errors: res.data.errors || 0,
          log: res.data.log || []
        }
        this.stopProgress()
        this.progressPercent = 100
        await new Promise(resolve => setTimeout(resolve, 300))
        this.processing = false
        this.$emit('usersImported')
      } catch (err) {
        this.stopProgress()
        this.processing = false
        const msg = err.response && err.response.data ? err.response.data.message : err.message
        errorMessage('Alerta: ' + msg)
        this.step = 2
      }
    },
    startProgress () {
      this.progressTimer = setInterval(() => {
        if (this.progressPercent < 90) {
          const remaining = 90 - this.progressPercent
          const increment = Math.max(0.5, remaining * 0.08)
          this.progressPercent = Math.min(90, Math.round((this.progressPercent + increment) * 10) / 10)
        }
      }, 200)
    },
    stopProgress () {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    finish () {
      this.modal = false
    },
    close () {
      this.modal = false
      this.file = null
      this.step = 1
    }
  },
  watch: {
    modal (val) {
      if (!val) {
        this.file = null
        this.step = 1
        this.stopProgress()
      }
    }
  },
  beforeDestroy () {
    this.stopProgress()
  }
}
</script>

<style scoped>
.import-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(85, 110, 230, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.import-icon-wrapper i {
  font-size: 28px;
  color: #556ee6;
}
.import-icon-success {
  background-color: rgba(52, 195, 143, 0.1) !important;
}
.import-icon-success i {
  color: #34c38f !important;
}
.import-icon-warning {
  background-color: rgba(241, 180, 76, 0.1) !important;
}
.import-icon-warning i {
  color: #f1b44c !important;
}
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.step-number {
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 50%;
  background-color: #556ee6;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.drop-zone {
  border: 2px dashed #ced4da;
  border-radius: 8px;
  padding: 30px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
}
.drop-zone:hover {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.03);
}
.drop-zone-active {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.06);
}
.drop-zone-has-file {
  border-color: #34c38f;
  border-style: solid;
  background: rgba(52, 195, 143, 0.03);
}
.drop-zone-icon {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}
.result-card {
  text-align: center;
  padding: 16px 28px;
  border-radius: 8px;
  min-width: 120px;
}
.result-card-success {
  background: rgba(52, 195, 143, 0.1);
}
.result-card-danger {
  background: rgba(244, 106, 106, 0.1);
}
.result-number {
  display: block;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}
.result-card-success .result-number {
  color: #34c38f;
}
.result-card-danger .result-number {
  color: #f46a6a;
}
.result-label {
  display: block;
  font-size: 12px;
  color: #74788d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}
.table-sm td, .table-sm th {
  padding: 0.4rem 0.6rem;
}
code {
  font-size: 12px;
}
.vld-overlay.is-full-page {
  z-index: 1051 !important;
}
</style>
